<template>
    <div class="deviceSearch-heroshot mb-4 listing">
        <h1 class="d-block h2 mb-3 mb-lg-5 pb-0">Blitzschnell die passende Sägekette finden</h1>
        <!-- Parallax Banner -->
        <div class="parallax-banner">
            <div class="parallax-layer layer1"></div>
            <div class="parallax-layer layer2"></div>
            <!-- Optional Overlay -->
            <div class="parallax-overlay"></div>
        </div>
        <!-- Dropdown Container -->
        <div class="dropdown-container" id="seachCategoriesDropdown" :class="additionalClass">
            <!-- Manufacturer Dropdown -->
            <div class="dropdown">
                <div class="dropdown-selected" @click="toggleDropdown('manufacturer')">
                    {{
                        selectedManufacturer
                            ? selectedManufacturer.lang.de.name
                            : "Hersteller auswählen"
                    }}
                </div>
                <transition name="slide-fade">
                    <div class="dropdown-options" v-if="showDropdown.manufacturer">
                        <input v-model="manufacturerSearch" type="text" placeholder="Hersteller suchen..."
                            @click.stop />
                        <div class="dropdown-option" v-for="manufacturer in filteredManufacturers"
                            :key="manufacturer.id" @click.stop="selectManufacturer(manufacturer)">
                            {{ manufacturer.lang.de.name }}
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Model Dropdown -->
            <div class="dropdown" :class="{ disabled: !selectedManufacturer }">
                <div class="dropdown-selected" @click="toggleDropdown('model')">
                    {{ selectedModel ? selectedModel.lang.de.name : "Modell auswählen" }}
                </div>
                <transition name="slide-fade">
                    <div class="dropdown-options" v-if="showDropdown.model">
                        <input v-model="modelSearch" type="text" placeholder="Modell suchen..." @click.stop />
                        <div class="dropdown-option" v-for="model in filteredModels" :key="model.id"
                            @click.stop="selectModel(model)">
                            {{ model.lang.de.name }}
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Facet Dropdown -->
            <div class="dropdown" :class="{ disabled: !selectedModel }">
                <div class="dropdown-selected" @click="toggleDropdown('facet')">
                    {{
                        selectedFacet
                            ? selectedFacet.facet_value_name_de
                            : "Arbeitslänge auswählen"
                    }}
                </div>
                <transition name="slide-fade">
                    <div class="dropdown-options" v-if="showDropdown.facet">
                        <div class="dropdown-option" v-for="facet in filteredFacets" :key="facet.facet_url_parameter_id"
                            @click.stop="selectFacet(facet)">
                            {{ facet.facet_value_name_de }}
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "sageketten-search",
    props: {
        additionalClass: {
            type: String,
            required: false,
            default: "",
        },
    },
    data() {
        return {
            manufacturers: [],
            models: [],
            facets: [],
            selectedManufacturer: null,
            selectedModel: null,
            selectedFacet: null,
            showDropdown: {
                manufacturer: false,
                model: false,
                facet: false,
            },
            manufacturerSearch: "",
            modelSearch: "",
            facetSearch: "",
            astScrollTop: 0,  // Add this to track scroll direction
            scrollSpeed: 0.4,  // Add this to control scroll speed
        };
    },
    computed: {
        filteredManufacturers() {
            if (Array.isArray(this.manufacturers)) {
                return this.manufacturers.filter((manufacturer) =>
                    manufacturer.lang.de.name
                        .toLowerCase()
                        .includes(this.manufacturerSearch.toLowerCase())
                );
            } else {
                console.error(
                    "this.manufacturers is not an array:",
                    this.manufacturers
                );
                return [];
            }
        },
        filteredModels() {
            return this.models.filter((model) =>
                model.lang.de.name
                    .toLowerCase()
                    .includes(this.modelSearch.toLowerCase())
            );
        },
        filteredFacets() {
            return this.facets.filter((facet) =>
                facet.facet_value_name_de
                    .toLowerCase()
                    .includes(this.facetSearch.toLowerCase())
            );
        },
        constructedUrl() {
            if (
                this.selectedManufacturer &&
                this.selectedModel &&
                this.selectedFacet
            ) {
                const manufacturerSlug =
                    this.selectedManufacturer.lang.de.url.split("/")[4];
                const modelSlug = this.selectedModel.lang.de.url.split("/")[5];
                const facetId = this.selectedFacet.facet_url_parameter_id;
                return `https://www.ma-versand.de/saegeketten-schwerter/${manufacturerSlug}/${modelSlug}/?facets=${facetId}`;
            }
            return null;
        },
    },
    methods: {
        handleScroll() {
            requestAnimationFrame(() => {
                const scrollTop = window.pageYOffset;
                const layer1 = this.$el.querySelector('.layer1');
                const layer2 = this.$el.querySelector('.layer2');
                const isDesktop = window.innerWidth > 600;

                if (layer1 && layer2) {
                    const scrollDirection = scrollTop > this.lastScrollTop ? 1 : -1;
                    const scrollDistance = Math.abs(scrollTop - this.lastScrollTop);

                    // Calculate transforms with synchronized speeds
                    const verticalOffset = isDesktop ? 70 : 0;
                    const transform1 = (scrollTop * this.scrollSpeed) + verticalOffset;
                    const transform2 = (scrollTop * this.scrollSpeed) + verticalOffset;
                    const layer2Offset = isDesktop ? -40 : 0;

                    // Apply transforms with GPU acceleration, adding 60px X offset to layer2
                    layer1.style.transform = `translate3d(0, ${transform1}px, -1px) scale(1.99)`;
                    layer2.style.transform = `translate3d(${layer2Offset}px, ${transform2}px, -1px) scale(1.99)`; // Added 60px X offset here

                    this.lastScrollTop = scrollTop;
                }
            });
        },

        initializeParallax() {
            const banner = this.$el.querySelector('.parallax-banner');
            const isDesktop = window.innerWidth > 600;

            if (banner) {
                banner.style.willChange = 'transform';
                const layers = banner.querySelectorAll('.parallax-layer');
                const initialOffset = isDesktop ? 120 : 0;
                const layer2Offset = isDesktop ? -40 : 0;

                // Set initial transforms with 60px X offset for layer2
                layers[0].style.transform = `translate3d(0, ${initialOffset}px, -1px) scale(1.99)`;
                layers[1].style.transform = `translate3d( ${layer2Offset}px ,${initialOffset}px, -1px) scale(1.99)`;
            }
        },
        toggleDropdown(dropdown) {
            if (
                (dropdown === "model" && !this.selectedManufacturer) ||
                (dropdown === "facet" && !this.selectedModel)
            ) {
                return;
            }
            // Close other dropdowns
            Object.keys(this.showDropdown).forEach((key) => {
                if (key !== dropdown) {
                    this.showDropdown[key] = false;
                }
            });
            // Toggle the selected dropdown
            this.showDropdown[dropdown] = !this.showDropdown[dropdown];
        },
        async fetchManufacturers() {
            try {
                const response = await axios.get(
                    "https://ma-versand.webimpact.app/api/saegeketten/json/root"
                );
                if (response && response.data) {
                    this.manufacturers = response.data;
                } else {
                    console.error("No data received from fetchManufacturers");
                }
            } catch (error) {
                console.error("Error fetching manufacturers:", error);
            }
        },
        async selectManufacturer(manufacturer) {
            this.selectedManufacturer = manufacturer;
            this.selectedModel = null;
            this.selectedFacet = null;
            this.models = [];
            this.facets = [];
            this.modelSearch = "";
            this.facetSearch = "";
            this.showDropdown.manufacturer = false;

            try {
                const response = await axios.get(
                    `https://ma-versand.webimpact.app/api/saegeketten/json/${manufacturer.id}`
                );
                if (response && response.data) {
                    this.models = response.data;
                } else {
                    console.error("No data received from selectManufacturer");
                }
            } catch (error) {
                console.error("Error fetching models:", error);
            }
        },
        async selectModel(model) {
            this.selectedModel = model;
            this.selectedFacet = null;
            this.facets = Object.values(model.facets || {});
            this.facetSearch = "";
            this.showDropdown.model = false;
        },
        selectFacet(facet) {
            this.selectedFacet = facet;
            this.showDropdown.facet = false;

            if (this.constructedUrl) {
                // Show the loading animation
                const loadingEl = document.getElementById('loading');
                if (loadingEl) {
                    loadingEl.style.display = 'flex';
                }

                // Delay the redirect slightly to let the animation show up
                setTimeout(() => {
                    window.location.href = this.constructedUrl;
                }, 200);
            } else {
                console.warn("Constructed URL is not available.");
            }
        }
    },
    created() {
        this.fetchManufacturers();
    },
    mounted() {
        this.initializeParallax();
        window.addEventListener('scroll', this.handleScroll, { passive: true });
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.handleScroll);
    },
};
</script>
